import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, ChefHat, Loader2, Edit, Trash2, Users, Radio, Send, Bell } from "lucide-react";

const FORMATTER = new Intl.DateTimeFormat("it-IT", { day: "2-digit", month: "short", year: "numeric" });

const TIME_FMT = new Intl.DateTimeFormat("it-IT", {
  hour: "2-digit", minute: "2-digit", second: "2-digit",
});

export default function PreparatoreDashboard() {
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [stream, setStream] = useState([]);  // recent orders for live feed
  const [resending, setResending] = useState(null);
  const sinceRef = useRef(null);
  const knownIdsRef = useRef(new Set());

  const fetchAll = async () => {
    try {
      const [dRes, sRes] = await Promise.all([
        api.get("/dishes/mine"),
        api.get(`/orders/summary/${new Date().toISOString().slice(0, 10)}`),
      ]);
      setDishes(dRes.data);
      setSummary(sRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // Live stream polling
  const fetchStream = async (initial = false) => {
    try {
      const params = !initial && sinceRef.current ? `?since=${encodeURIComponent(sinceRef.current)}` : "";
      const { data } = await api.get(`/orders/recent${params}`);
      sinceRef.current = data.now;
      if (initial) {
        // Seed known IDs from the latest 50 so we don't toast the historical ones
        for (const o of data.orders) knownIdsRef.current.add(o.id);
        setStream(data.orders.slice(0, 20));
        return;
      }
      // New since last poll
      const fresh = data.orders.filter((o) => !knownIdsRef.current.has(o.id));
      for (const o of fresh) knownIdsRef.current.add(o.id);
      if (fresh.length > 0) {
        setStream((prev) => [...fresh, ...prev].slice(0, 20));
        // Toast each new order
        for (const o of fresh) {
          toast.success(`📥 ${o.user_name} × ${o.portions} — ${o.dish_name}`, {
            description: `€ ${(o.dish_price * o.portions).toFixed(2)}`,
          });
        }
        // Refresh summary in background
        fetchAll();
      }
    } catch (e) {
      // silent — don't spam errors on polling
      console.error("stream poll failed", e);
    }
  };

  useEffect(() => {
    fetchAll();
    fetchStream(true);
    const t = setInterval(() => fetchStream(false), 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo piatto?")) return;
    try {
      await api.delete(`/dishes/${id}`);
      toast.success("Piatto eliminato");
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const resendSummary = async (id) => {
    setResending(id);
    try {
      await api.post(`/dishes/${id}/resend-summary`);
      toast.success("Riepilogo inviato su Telegram");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResending(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-6 sm:py-10">
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground uppercase tracking-widest font-medium mb-2">
            <ChefHat className="w-4 h-4" /> Cucina
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
            Dashboard preparatore
          </h1>
          <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1.5" data-testid="live-indicator">
            <Radio className="w-3 h-3 text-primary animate-pulse" />
            <span>Aggiornamento automatico ogni 8 secondi</span>
          </p>
        </div>
        <Link
          to="/preparatore/piatti/nuovo"
          data-testid="new-dish-button"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-white font-medium rounded-md hover:bg-[#436343] active:scale-[0.98] transition-all"
        >
          <Plus className="w-4 h-4" /> Nuovo piatto
        </Link>
      </div>

      {/* Quick links */}
      <div className="flex gap-3 mb-8 flex-wrap">
        <Link
          to="/preparatore/ricorrenti"
          data-testid="recurring-link"
          className="inline-flex items-center gap-2 px-4 py-2 bg-card border border-border text-sm font-medium rounded-md hover:border-primary hover:text-primary transition-all"
        >
          🔁 Menu settimanale ricorrente
        </Link>
      </div>

      {/* Live stream */}
      {stream.length > 0 && (
        <section className="mb-10" data-testid="live-stream">
          <h2 className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-3 flex items-center gap-1.5">
            <Bell className="w-3.5 h-3.5 text-primary" /> Ultime prenotazioni
          </h2>
          <div className="bg-card border border-border rounded-lg divide-y divide-border max-h-80 overflow-auto">
            {stream.map((o, idx) => (
              <StreamRow key={o.id} order={o} fresh={idx === 0 && knownIdsRef.current.size > 0} />
            ))}
          </div>
        </section>
      )}

      {/* Today summary */}
      {summary && summary.items.length > 0 && (
        <section className="mb-10">
          <h2 className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-3">
            Riepilogo di oggi
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="today-summary">
            {summary.items.map((it) => (
              <div key={it.dish.id} className="bg-card border border-border rounded-lg p-5">
                <div className="flex items-start justify-between mb-3 gap-3">
                  <div className="min-w-0">
                    <div className="font-display font-medium text-lg truncate">{it.dish.name}</div>
                    <div className="text-xs text-muted-foreground">
                      Cutoff {it.dish.cutoff_date || it.dish.date} {it.dish.cutoff_time} · € {Number(it.dish.price).toFixed(2)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-2xl font-semibold text-primary">
                      {it.total_portions}
                    </div>
                    <div className="text-xs text-muted-foreground">porzioni</div>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground mb-3 flex items-center gap-3 flex-wrap">
                  <span>{it.num_orders} prenotazioni</span>
                  <span>·</span>
                  <span>€ {Number(it.total_revenue).toFixed(2)} totale</span>
                  <span>·</span>
                  <span>{it.dish.portions_available} disp.</span>
                </div>
                {it.orders.length > 0 && (
                  <div className="border-t border-border pt-3">
                    <div className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                      <Users className="w-3 h-3" /> Chi ha prenotato
                    </div>
                    <ul className="space-y-1 mb-3">
                      {it.orders.map((o) => (
                        <li key={o.id} className="text-sm flex justify-between">
                          <span>
                            {o.user_name}
                            {o.needs_cutlery && (
                              <span className="ml-1.5 text-xs text-primary" title="Posate richieste">🍴</span>
                            )}
                          </span>
                          <span className="text-muted-foreground">× {o.portions}</span>
                        </li>
                      ))}
                    </ul>
                    {it.orders.some((o) => o.needs_cutlery) && (
                      <div className="text-xs text-primary mb-3" data-testid={`cutlery-count-${it.dish.id}`}>
                        🍴 Posate da preparare: <b>{it.orders.filter((o) => o.needs_cutlery).length}</b>
                      </div>
                    )}
                  </div>
                )}
                <button
                  onClick={() => resendSummary(it.dish.id)}
                  disabled={resending === it.dish.id}
                  data-testid={`resend-summary-${it.dish.id}`}
                  className="w-full mt-1 px-3 py-2 text-xs border border-primary/30 text-primary rounded-md hover:bg-primary hover:text-white active:scale-[0.98] disabled:opacity-50 transition-all inline-flex items-center justify-center gap-1.5"
                >
                  <Send className="w-3.5 h-3.5" />
                  {resending === it.dish.id ? "Invio…" : "Reinvia riepilogo via Telegram"}
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* All dishes */}
      <section>
        <h2 className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-3">
          Tutti i tuoi piatti
        </h2>
        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento…
          </div>
        ) : dishes.length === 0 ? (
          <div className="bg-card border border-border rounded-lg p-8 text-center" data-testid="dishes-empty">
            <p className="text-muted-foreground mb-4">Non hai ancora pubblicato nessun piatto.</p>
            <Link to="/preparatore/piatti/nuovo" className="text-primary font-medium hover:underline">
              Pubblica il tuo primo piatto →
            </Link>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/40">
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Piatto</th>
                  <th className="px-4 py-3 font-medium hidden sm:table-cell">Data</th>
                  <th className="px-4 py-3 font-medium">Disp.</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">Cutoff</th>
                  <th className="px-4 py-3 font-medium">Prezzo</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {dishes.map((d) => (
                  <tr key={d.id} className="border-t border-border" data-testid={`dish-row-${d.id}`}>
                    <td className="px-4 py-3 font-medium">{d.name}{!d.active && <span className="ml-2 text-xs text-muted-foreground">(disattivato)</span>}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground hidden sm:table-cell">
                      {FORMATTER.format(new Date(d.date))}
                    </td>
                    <td className="px-4 py-3 text-sm">{d.portions_available}/{d.portions_total}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground hidden md:table-cell">
                      {(d.cutoff_date && d.cutoff_date !== d.date) ? `${d.cutoff_date} ` : ""}{d.cutoff_time}
                    </td>
                    <td className="px-4 py-3 text-sm">€ {Number(d.price).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <Link
                          to={`/preparatore/piatti/${d.id}/modifica`}
                          data-testid={`edit-dish-${d.id}`}
                          className="p-2 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                        >
                          <Edit className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => remove(d.id)}
                          data-testid={`delete-dish-${d.id}`}
                          className="p-2 rounded hover:bg-accent/10 text-muted-foreground hover:text-accent"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function StreamRow({ order, fresh }) {
  const total = order.dish_price ? (order.dish_price * order.portions).toFixed(2) : "—";
  const time = order.created_at ? TIME_FMT.format(new Date(order.created_at)) : "";
  return (
    <div
      className={`px-4 py-3 flex items-center justify-between gap-3 ${fresh ? "bg-primary/5 animate-fade-in" : ""}`}
      data-testid={`stream-row-${order.id}`}
    >
      <div className="min-w-0">
        <div className="text-sm font-medium truncate">
          <span className="text-primary">{order.user_name}</span> × {order.portions} — {order.dish_name}
        </div>
        <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
          <span>{time}</span>
          <span>·</span>
          <span>€ {total}</span>
          <span>·</span>
          <span className="uppercase tracking-wide opacity-70 text-[10px]">via {order.source}</span>
        </div>
      </div>
    </div>
  );
}
